/**
 * API Client for Noesis Mental Health System
 * Handles all backend API communication with authentication
 */
import { logger } from './logger';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8002';

export type ApiResponse<T> = {
  data?: T;
  error?: string;
  status: number;
};

/**
 * 401 全局处理：登录态失效（token 过期 / 被管理员强制下线）时，
 * 清除本地凭证并跳回登录页。
 * 注意：不能复用 lib/auth 的 clearAuthData —— auth.ts 反向依赖本文件，会形成循环引用。
 */
function handleUnauthorized() {
  if (typeof window === 'undefined') return;
  const role = sessionStorage.getItem('user_role');
  sessionStorage.removeItem('auth_token');
  sessionStorage.removeItem('auth_user');
  sessionStorage.removeItem('user_role');
  sessionStorage.removeItem('user_name');
  sessionStorage.removeItem('user_id');
  sessionStorage.removeItem('patient_session_id');
  sessionStorage.removeItem('patient_name');
  sessionStorage.removeItem('patient_session_info');
  sessionStorage.removeItem('patient_id_card');
  const loginPath = role === 'patient' ? '/patient/login' : '/doctor/login';
  // 已在登录页时只清凭证、不再跳转，避免（心跳等触发的）401 造成重定向循环
  if (window.location.pathname === '/doctor/login' || window.location.pathname === '/patient/login')
    return;
  window.location.href = `${loginPath}?timeout=true`;
}

/**
 * API Client class for making authenticated requests
 */
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get the authentication token from sessionStorage
   */
  private getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem('auth_token');
  }

  /**
   * Set the authentication token
   */
  setToken(token: string): void {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('auth_token', token);
    }
  }

  /**
   * Clear the authentication token
   */
  clearToken(): void {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('user_role');
      sessionStorage.removeItem('user_name');
      sessionStorage.removeItem('user_id');
    }
  }

  /**
   * Make an authenticated API request
   */
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const token = this.getToken();
    const url = `${this.baseUrl}${endpoint}`;
    const startTime = Date.now();

    // Log request
    const method = options.method || 'GET';
    logger.api(method as string, endpoint, 'request', { hasToken: !!token });

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const duration = Date.now() - startTime;
      const data = await response.json().catch(() => null);

      // Log response
      logger.api(method as string, endpoint, response.status, { duration, hasData: !!data });

      if (!response.ok) {
        // 登录态失效（token 过期 / 被管理员强制下线）→ 清凭证并跳登录页；
        // 登录与改密接口自身的 401 属于凭据错误，不触发跳转
        if (
          response.status === 401 &&
          endpoint !== '/api/auth/login' &&
          endpoint !== '/api/patient/login' &&
          endpoint !== '/api/auth/emergency-login' &&
          endpoint !== '/api/auth/change-password'
        ) {
          handleUnauthorized();
        }
        logger.warn(`API Error: ${method} ${endpoint} - ${response.status}`, {
          error: data?.detail || data?.error,
          response: data,
        });
        return {
          error: data?.detail || data?.error || 'Request failed',
          status: response.status,
        };
      }

      return {
        data,
        status: response.status,
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      logger.error(`API Error: ${method} ${endpoint} - Network error`, {
        duration,
        error: error instanceof Error ? error.message : String(error),
      });
      return {
        error: error instanceof Error ? error.message : 'Network error',
        status: 0,
      };
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// ==================== Auth APIs ====================

export interface LoginRequest {
  username: string;
  password: string;
  role?: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: {
    id: number;
    username: string;
    role: string;
    full_name: string;
  };
  /** 后端返回是否需要强制改密（首次登录），缺省视为 false */
  must_change_password?: boolean;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

export interface PatientLoginRequest {
  id_card: string;
  phone_last4: string;
}

export interface PatientLoginResponse {
  access_token: string;
  token_type: string;
  patient_session_id: number;
  session_id: string;
  expires_at: string;
  patient_info: {
    full_name: string;
    remaining_minutes?: number;
  };
}

export const authApi = {
  login: (data: LoginRequest) => apiClient.post<LoginResponse>('/api/auth/login', data),

  patientLogin: (data: PatientLoginRequest) =>
    apiClient.post<PatientLoginResponse>('/api/patient/login', data),

  changePassword: (data: { current_password: string; new_password: string }) =>
    apiClient.post<ChangePasswordResponse>('/api/auth/change-password', data),

  logout: () => apiClient.post<{ success: boolean }>('/api/auth/logout', {}),

  refresh: () =>
    apiClient.post<{ access_token: string; token_type: string }>('/api/auth/refresh', {}),

  emergencyLogin: (data: { username: string; password: string; totp_code: string }) =>
    apiClient.post<{
      access_token: string;
      token_type: string;
      user: {
        id: number;
        username: string;
        role: string;
        full_name: string;
        is_emergency: boolean;
      };
      expires_in_minutes: number;
    }>('/api/auth/emergency-login', data),

  getMe: () => apiClient.get('/api/auth/me'),
};

// ==================== Emergency Account Admin APIs（应急账号/破玻璃） ====================

export interface EmergencyStatus {
  provisioned: boolean;
  user_id?: number;
  username?: string;
  full_name?: string;
  enabled: boolean;
  active_in_window: boolean;
  enabled_at?: string | null;
  expires_at?: string | null;
  enabled_by_user_id?: number | null;
  enabled_by_username?: string | null;
  enabled_reason?: string | null;
  last_login_at?: string | null;
  review_required?: boolean;
}

export interface EmergencyEnableResult {
  username: string;
  temporary_password: string;
  totp_secret: string;
  totp_uri: string;
  expires_at: string;
  user_id: number;
}

export interface EmergencyAccessLog {
  id: number;
  event_time: string;
  event_type: string;
  actor_user_id: number;
  actor_username: string;
  induced_by_user_id?: number | null;
  session_jti?: string | null;
  http_method?: string | null;
  http_path?: string | null;
  query_string?: string | null;
  request_body_hash?: string | null;
  response_status?: number | null;
  response_duration_ms?: number | null;
  affected_patient_ids?: string | null;
  affected_record_count?: number | null;
  client_ip?: string | null;
  user_agent?: string | null;
  notes?: string | null;
}

export const emergencyApi = {
  enable: (data: { reason: string; duration_minutes: number }) =>
    apiClient.post<EmergencyEnableResult>('/api/admin/emergency/enable', data),

  disable: () => apiClient.post<{ success: boolean }>('/api/admin/emergency/disable', {}),

  status: () => apiClient.get<EmergencyStatus>('/api/admin/emergency/status'),

  listAccessLogs: (params: {
    offset?: number;
    limit?: number;
    actor_user_id?: number;
    event_type?: string;
    start_time?: string;
    end_time?: string;
    session_jti?: string;
  }) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    return apiClient.get<{ items: EmergencyAccessLog[]; total: number }>(
      `/api/audit/emergency-access${q ? `?${q}` : ''}`
    );
  },

  getAccessLog: (logId: number) =>
    apiClient.get<EmergencyAccessLog>(`/api/audit/emergency-access/${logId}`),

  reviewLog: (logId: number, note: string) =>
    apiClient.post<{ success: boolean }>(`/api/audit/emergency-access/${logId}/review`, {
      note,
    }),

  listPendingReviews: () =>
    apiClient.get<{ items: Record<string, unknown>[]; total: number }>(
      '/api/audit/emergency-pending-reviews'
    ),
};

// ==================== Access Logs（通用访问日志/日志管理） ====================

export interface AccessLog {
  id: number;
  event_time: string;
  event_category: string;
  event_type: string;
  severity: string;
  actor_role?: string | null;
  actor_user_id?: number | null;
  actor_phone_masked?: string | null;
  session_jti?: string | null;
  http_method?: string | null;
  http_path?: string | null;
  http_status?: number | null;
  duration_ms?: number | null;
  client_ip?: string | null;
  user_agent?: string | null;
  target_resource_type?: string | null;
  target_resource_id?: string | null;
  detail?: string | null;
  created_at?: string;
}

export interface AccessLogStats {
  days: number;
  by_category: Array<{ event_category: string; n: number }>;
  by_type: Array<{ event_type: string; n: number }>;
  by_severity: Array<{ severity: string; n: number }>;
}

export interface AccessLogQueryParams {
  offset?: number;
  limit?: number;
  event_category?: string;
  event_type?: string;
  actor_role?: string;
  actor_user_id?: number;
  severity?: string;
  start_time?: string;
  end_time?: string;
  keyword?: string;
  target_resource_type?: string;
  target_resource_id?: string;
  session_jti?: string;
}

function buildAccessLogQS(params: object): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
  });
  const q = qs.toString();
  return q ? `?${q}` : '';
}

export const accessLogApi = {
  list: (params: AccessLogQueryParams) =>
    apiClient.get<{ items: AccessLog[]; total: number }>(
      `/api/audit/access-logs${buildAccessLogQS(params)}`
    ),

  detail: (logId: number) => apiClient.get<AccessLog>(`/api/audit/access-logs/${logId}`),

  stats: (days: number = 7) =>
    apiClient.get<AccessLogStats>(`/api/audit/access-logs/stats?days=${days}`),

  /**
   * 导出下载 URL（带 format 参数）。页面自行 fetch（附 Bearer）+ blob 落盘 ——
   * Bearer token 无法随顶层导航发送，直链打开会 401。
   */
  exportUrl: (params: AccessLogQueryParams, format: 'csv' | 'json') =>
    `${API_BASE_URL}/api/audit/access-logs/export${buildAccessLogQS({ ...params, format })}`,
};

// ==================== Admin / Hospital Admin APIs ====================

export interface AdminUserListItem {
  id: number;
  username: string;
  role: 'admin' | 'system_admin' | 'hospital_admin' | 'doctor' | 'emergency';
  full_name: string | null;
  hospital_id: number | null;
  hospital_name: string | null;
  is_active: boolean;
  sessions_invalidated_at: string | null;
  last_login_at: string | null;
  created_at: string | null;
}

export const adminApi = {
  getSystemOverview: () => apiClient.get('/api/system/stats/overview'),

  listHospitals: () => apiClient.get('/api/admin/hospitals'),

  createHospital: (data: { name: string; code?: string }) =>
    apiClient.post('/api/admin/hospitals', data),

  updateHospitalStatus: (hospitalId: number, isActive: boolean) =>
    apiClient.post(`/api/admin/hospitals/${hospitalId}/status`, { is_active: isActive }),

  updateHospital: (hospitalId: number, data: { name: string }) =>
    apiClient.post(`/api/admin/hospitals/${hospitalId}/update`, data),

  /** 系统管理员：强制下线某医院全部非患者活跃用户 */
  revokeHospitalSessions: (hospitalId: number, reason?: string) =>
    apiClient.post<{
      hospital_id: number;
      hospital_name: string;
      revoked_user_count: number;
      sessions_invalidated_at: string | null;
      message: string;
    }>(`/api/admin/hospitals/${hospitalId}/revoke-sessions`, { reason: reason ?? null }),

  /** 系统管理员：列出所有非 patient 用户 */
  listUsers: (role?: string) =>
    apiClient.get<{ users: AdminUserListItem[] }>(`/api/admin/users${role ? `?role=${role}` : ''}`),

  /** 系统管理员强制注销某用户的所有活跃会话 */
  revokeUserSessions: (userId: number, reason?: string) =>
    apiClient.post<{
      user_id: number;
      username: string;
      sessions_invalidated_at: string;
      message: string;
    }>(`/api/admin/users/${userId}/revoke-sessions`, { reason: reason ?? null }),
};

export const hospitalAdminApi = {
  getOverview: () => apiClient.get('/api/hospital/stats/overview'),

  getProfile: () => apiClient.get('/api/hospital/profile'),

  listDoctors: () => apiClient.get('/api/hospital/users'),

  createDoctor: (data: {
    username: string;
    password: string;
    full_name: string;
    license_number?: string;
    department?: string;
    title?: string;
  }) => apiClient.post('/api/hospital/doctors', data),

  updateDoctorStatus: (doctorId: number, isActive: boolean) =>
    apiClient.post(`/api/hospital/doctors/${doctorId}/status`, { is_active: isActive }),

  /** 医院管理员强制注销本院某用户的所有活跃会话 */
  revokeUserSessions: (userId: number, reason?: string) =>
    apiClient.post<{
      user_id: number;
      username: string;
      sessions_invalidated_at: string;
      message: string;
    }>(`/api/hospital/users/${userId}/revoke-sessions`, { reason: reason ?? null }),
};

// ==================== Patient APIs ====================

export interface LinkSessionRequest {
  session_id: string;
  patient_id_card: string;
}

export interface LinkSessionResponse {
  status: string;
  message: string;
  session_id: string;
  patient_name: string;
  patient_id: number;
}

export const patientApi = {
  linkSession: (data: LinkSessionRequest) =>
    apiClient.post<LinkSessionResponse>('/api/patient/link-session', data),
};

// ==================== Doctor APIs ====================

export interface PatientCreateRequest {
  id_card: string;
  full_name: string;
  gender?: string;
  birth_date?: string;
  phone_number: string;
  scale_id?: number;
}

export interface PatientSessionResponse {
  patient_id: number;
  patient_session_id: number;
  session_id: string;
  access_status: string;
  qr_login_url: string;
  message?: string;
}

export interface PatientSession {
  id: number;
  /** 建行时尚未生成 room name 时为 null（患者开始会话后才生成），前端据此灰置"查看详情" */
  session_id: string | null;
  patient_id: number;
  doctor_id: number;
  scale_id?: number;
  status: string;
  access_status: string;
  access_enabled_at?: string;
  access_enabled_by?: number;
  access_expires_at?: string;
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  patient_name: string;
  patient_id_card: string;
  doctor_name: string;
  scale_name?: string;
  /** 'scale_guided'（量表引导）或 'free_chat'（自由聊天，无量表） */
  conversation_mode?: 'scale_guided' | 'free_chat';
  /** sessions 表是否已有数据行（患者真正进房后才有）。登录即分配房间号但从未
   *  进房的会话无数据行，详情页会 404——与 session_id 共同决定"查看详情"可点性 */
  has_session_data?: boolean;
}

export interface EnableSessionRequest {
  duration_minutes?: number;
}

export interface EnableSessionResponse {
  patient_session_id: number;
  session_id: string;
  access_status: string;
  access_enabled_at: string;
  access_expires_at: string;
  remaining_minutes: number;
  qr_login_url: string;
  message?: string;
}

export interface ExtendSessionRequest {
  additional_minutes: number;
}

export const doctorApi = {
  createPatient: (data: PatientCreateRequest) =>
    apiClient.post<PatientSessionResponse>('/api/doctor/patients', data),

  getSessions: (params?: {
    access_status?: string;
    page?: number;
    page_size?: number;
    /** 通用模糊搜索：患者名/医生名/session_id/行 id（应急工作台在用） */
    search?: string;
    /** 患者号/身份证号模糊搜索（患者管理页搜索区） */
    search_patient_id?: string;
    start_date?: string;
    end_date?: string;
  }) => {
    const queryString = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              acc[key] = String(value);
            }
            return acc;
          }, {})
        ).toString()
      : '';
    return apiClient.get<{ sessions: PatientSession[]; total_count: number }>(
      `/api/doctor/sessions${queryString}`
    );
  },

  getSessionStats: () =>
    apiClient.get<{
      all: number;
      disabled: number;
      enabled: number;
      active: number;
      completed: number;
    }>('/api/doctor/sessions/stats'),

  enableSession: (patientSessionId: number, data?: EnableSessionRequest) =>
    apiClient.post<EnableSessionResponse>(
      `/api/doctor/sessions/${patientSessionId}/enable`,
      data || {}
    ),

  disableSession: (patientSessionId: number) =>
    apiClient.post<{ patient_session_id: number; access_status: string; message: string }>(
      `/api/doctor/sessions/${patientSessionId}/disable`,
      {}
    ),

  extendSession: (patientSessionId: number, data: ExtendSessionRequest) =>
    apiClient.post<{
      patient_session_id: number;
      access_expires_at: string;
      remaining_minutes: number;
      message: string;
    }>(`/api/doctor/sessions/${patientSessionId}/extend`, data),

  getAssessmentScales: () =>
    apiClient.get<{
      scales: Array<{ id: number; name: string; description: string; level: number }>;
    }>('/api/doctor/assessment-scales'),
};

// ==================== Scale & Assessment APIs (TODS) ====================

export interface Scale {
  id: number;
  name: string;
  description: string;
  level: number;
  question_count: number;
}

export interface ScaleQuestion {
  id: number;
  code: string;
  text: string;
  type: string;
  category: string;
  options: Array<{ value: number; label: string }>;
  display_order: number;
  is_required: boolean;
  metadata: Record<string, unknown>;
}

export interface ScaleDetails {
  id: number;
  name: string;
  description: string;
  level: number;
  total_questions: number;
  categories: Record<string, number>;
}

export interface ConversationState {
  patient_session_id: number;
  completed_slots: string[];
  pending_slots: string[];
  current_focus: string | null;
  conversation_mode: 'scale_guided' | 'free_chat';
  turn_count: number;
  completion_percentage: number;
  state: Record<string, unknown>;
}

export interface AssessmentProgress {
  scale_id: number;
  scale_name: string;
  conversation_mode: 'scale_guided' | 'free_chat';
  progress: {
    total_questions: number;
    completed_questions: number;
    completion_percentage: number;
    completed_slots: string[];
    pending_slots: string[];
    current_focus: string | null;
    turn_count: number;
  };
  state: Record<string, unknown>;
}

export const scalesApi = {
  // Get all available scales
  getScales: () => apiClient.get<{ scales: Scale[] }>('/api/scales'),

  // Get scale details
  getScaleDetails: (scaleId: number) =>
    apiClient.get<{ scale: ScaleDetails }>(`/api/scales/${scaleId}`),

  // Get scale questions
  getScaleQuestions: (scaleId: number) =>
    apiClient.get<{ scale_id: number; scale_name: string; questions: ScaleQuestion[] }>(
      `/api/scales/${scaleId}/questions`
    ),
};

export const conversationApi = {
  // Get conversation state
  getState: (sessionId: number) =>
    apiClient.get<{ state: ConversationState }>(`/api/sessions/${sessionId}/state`),

  // Switch conversation mode
  switchMode: (sessionId: number, mode: 'scale_guided' | 'free_chat') =>
    apiClient.patch<{ status: string; message: string; mode: string }>(
      `/api/sessions/${sessionId}/mode?mode=${mode}`
    ),

  // Get assessment progress
  getProgress: (sessionId: number) =>
    apiClient.get<AssessmentProgress>(`/api/sessions/${sessionId}/progress`),
};

// Add PATCH method to ApiClient class
ApiClient.prototype.patch = function <T>(endpoint: string, body: unknown): Promise<ApiResponse<T>> {
  return this.request<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

export default apiClient;
